import { getDescriptionMaxLengthError, isAdditionalCloseReasonValid } from '@opportunities/-lib/opportunityValidation'
import { useQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { type CloseReason, getCloseReasons } from '@/lib'
import { queryKeys } from '@/lib/queryKeys'

interface UseCloseReasonDialogOptions {
  open: boolean
  onSubmit: (data: { opportunityCloseReasonId: number; opportunityAdditionalCloseReason?: string | null }) => void
  onOpenChange: (open: boolean) => void
}

/**
 * クローズ理由をカテゴリでグループ化
 */
function groupCloseReasons(closeReasons: CloseReason[]): Map<string, CloseReason[]> {
  const groups = new Map<string, CloseReason[]>()

  for (const reason of closeReasons) {
    const category = reason.categoryName
    if (!groups.has(category)) {
      groups.set(category, [])
    }
    groups.get(category)?.push(reason)
  }

  return groups
}

export function useCloseReasonDialog({ open, onSubmit, onOpenChange }: UseCloseReasonDialogOptions) {
  const [selectedReasonId, setSelectedReasonId] = useState<number | null>(null)
  const [additionalReason, setAdditionalReason] = useState('')
  // 一度でも編集されたかどうかのフラグ
  const [touched, setTouched] = useState({ reason: false, additionalReason: false })

  // クローズ理由一覧取得
  const { data: closeReasonsData, isLoading } = useQuery({
    queryKey: queryKeys.closeReasons,
    queryFn: () => getCloseReasons(),
  })

  const closeReasons = closeReasonsData?.closeReasons || []
  const groupedReasons = groupCloseReasons(closeReasons)

  // 選択されたクローズ理由
  const selectedReason = closeReasons.find((r) => r.id === selectedReasonId)

  // ダイアログが開いたときにフォームをリセット
  useEffect(() => {
    if (open) {
      setSelectedReasonId(null)
      setAdditionalReason('')
      setTouched({ reason: false, additionalReason: false })
    }
  }, [open])

  // バリデーション結果（レンダリング時に直接計算）
  const closeReasonError = touched.reason && !selectedReasonId ? 'クローズ理由を選択してください' : null

  const additionalReasonError = (() => {
    if (!selectedReason?.isAdditionalCloseReasonRequired) return null
    if (!touched.additionalReason) return null
    const maxLengthError = getDescriptionMaxLengthError(additionalReason)
    if (maxLengthError) return maxLengthError
    if (!additionalReason.trim()) return 'クローズ理由（詳細）を入力してください'
    return null
  })()

  // 全エラーチェック（送信時のバリデーション用）
  const allHasErrors = (() => {
    if (!selectedReasonId) return true
    if (selectedReason?.isAdditionalCloseReasonRequired) {
      return !isAdditionalCloseReasonValid(additionalReason, true)
    }
    return false
  })()

  // エラーがあるかどうか（表示中のエラーベースでチェック）
  const hasErrors = !!closeReasonError || !!additionalReasonError

  const handleReasonChange = (reasonId: number | null) => {
    setSelectedReasonId(reasonId)
    setAdditionalReason('')
    setTouched((prev) => ({ ...prev, reason: true, additionalReason: false }))
  }

  const handleAdditionalReasonChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value
    setAdditionalReason(newValue)
    setTouched((prev) => ({ ...prev, additionalReason: true }))
  }

  const handleSubmit = () => {
    // 送信時に全フィールドを touched にする
    setTouched({ reason: true, additionalReason: true })

    if (allHasErrors || !selectedReasonId) {
      return
    }

    onSubmit({
      opportunityCloseReasonId: selectedReasonId,
      opportunityAdditionalCloseReason: selectedReason?.isAdditionalCloseReasonRequired ? additionalReason : undefined,
    })

    onOpenChange(false)
  }

  return {
    // State
    selectedReasonId,
    additionalReason,
    closeReasonError,
    additionalReasonError,
    hasErrors,

    // Data
    groupedReasons,
    selectedReason,
    isLoading,

    // Handlers
    handleReasonChange,
    handleAdditionalReasonChange,
    handleSubmit,
  }
}
